<template>
  <section class="sponsors-content mt-10 mb-10 px-4 lg:px-0" id="patrocinadores">
    <div class="max-w-screen-xl mx-auto">
      <!-- Título e subtítulo da seção -->
      <div class="mb-12">
        <div class="flex items-center gap-2 relative pb-3">
          <div>
            <h2
              class="text-lg md:text-xl font-medium tracking-wide"
              :style="{ color: customization?.section_title_color || '#1c243b' }"
            >
              {{ customization?.sponsors_title || 'Patrocinadores' }}
            </h2>
            <h2
              class="text-xs md:text-sm font-normal tracking-wide"
              :style="{ color: customization?.section_subtitle_color || '#666666' }"
            >
              {{ customization?.sponsors_subtitle || 'Veja quem nos apoia' }}
            </h2>
          </div>
          <div class="absolute bottom-0 left-0 w-full h-px bg-gray-200"></div>
          <div
            class="absolute bottom-0 left-0 h-px w-full max-w-[480px]"
            :style="{ backgroundColor: customization?.section_divider_color || '#e5e7eb' }"
          ></div>
        </div>
      </div>

      <!-- Conteúdo principal -->
      <div v-if="sponsors && sponsors.length > 0">
        <!-- Com categorias -->
        <div v-if="hasCategories">
          <div
            v-for="category in categorizedSponsors"
            :key="category.name"
            class="mb-8 last:mb-0"
          >
            <div
              class="grid gap-8 place-items-center"
              :class="gridClasses(category)"
            >
              <div
                v-for="sponsor in category.items"
                :key="sponsor.id"
                class="relative group bg-white/80 backdrop-blur-sm border border-gray-200 rounded-md hover:border-gray-300 hover:shadow-xl transition-all duration-300 overflow-hidden w-full"
                :class="cardHeightClasses(category)"
              >
                <!-- Tag da categoria com cor personalizada -->
                <div class="absolute top-3 left-3 z-10">
                  <span
                    class="inline-block px-4 py-1.5 text-xs font-light tracking-wider uppercase rounded-md border backdrop-blur-md transition-all duration-300"
                    :style="{
                      backgroundColor: (category.color || '#8b5cf6') + '18',
                      borderColor: (category.color || '#8b5cf6') + '40',
                      color: category.color || '#8b5cf6'
                    }"
                  >
                    {{ category.name }}
                  </span>
                </div>

                <!-- Logo do patrocinador (sempre colorido) -->
                <div class="w-full h-full flex items-center justify-center p-8 md:p-10">
                  <a
                    v-if="sponsor.website"
                    :href="sponsor.website"
                    target="_blank"
                    rel="noopener"
                    class="block w-full h-full flex items-center justify-center"
                  >
                    <img
                      :src="sponsor.logo"
                      :alt="sponsor.name"
                      class="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </a>
                  <img
                    v-else
                    :src="sponsor.logo"
                    :alt="sponsor.name"
                    class="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sem categorias (fallback) -->
        <div v-else class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 place-items-center">
          <div
            v-for="sponsor in sponsors"
            :key="sponsor.id"
            class="group bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl hover:border-gray-300 hover:shadow-xl transition-all duration-300 p-10 w-full h-56 flex items-center justify-center"
          >
            <a
              v-if="sponsor.website"
              :href="sponsor.website"
              target="_blank"
              rel="noopener"
              class="block w-full h-full flex items-center justify-center"
            >
              <img
                :src="sponsor.logo"
                :alt="sponsor.name"
                class="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
              />
            </a>
            <img
              v-else
              :src="sponsor.logo"
              :alt="sponsor.name"
              class="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </div>
      </div>

      <!-- Sem patrocinadores -->
      <div v-else class="text-center py-16">
        <p class="text-gray-500 text-lg">Nenhum patrocinador cadastrado ainda.</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useLandingPage } from '@/composables/useLandingPage'

const { sponsors, customization } = useLandingPage()

// Verifica se há categorias
const hasCategories = computed(() => sponsors.value?.some(s => s.category?.name))

// Agrupa e ordena: primeiro por size (1 antes de 2), depois por ID (ordem de criação)
const categorizedSponsors = computed(() => {
  if (!sponsors.value) return []

  const groups = {}

  sponsors.value.forEach(s => {
    const cat = s.category || { id: 999, name: 'Apoio', color: '#94a3b8', size: 2 }
    const key = cat.name

    if (!groups[key]) {
      groups[key] = {
        id: cat.id,
        name: cat.name,
        color: cat.color || '#94a3b8',
        size: parseInt(cat.size) || 2,
        items: []
      }
    }
    groups[key].items.push(s)
  })

  return Object.values(groups).sort((a, b) => {
    if (a.size !== b.size) {
      return a.size - b.size // 1 vem antes de 2
    }
    return a.id - b.id // mesma prioridade → ordem de criação
  })
})

// Define o grid conforme o size
const gridClasses = (category) => {
  return category.size === 1
    ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
    : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
}

// Define a altura do card conforme o size
const cardHeightClasses = (category) => {
  return category.size === 1
    ? 'h-64 md:h-72 lg:h-80'
    : 'h-56 md:h-64 lg:h-72'
}
</script>